import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layers3, LogOut, Zap, Sun, Moon, Menu, X, Users, ArrowLeftRight, Receipt, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { api } from "../lib/api";
import NotificationsBell from "./NotificationsBell";
import ActiveScanPill from "./ActiveScanPill";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { getTheme, setTheme } from "../lib/theme";

export default function Navbar() {
  const { user, logout } = useAuth();
  const currencyCtx = useCurrency();
  const navigate = useNavigate();
  const [theme, setThemeState] = useState(getTheme());
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingTrades, setPendingTrades] = useState(0);

  useEffect(() => {
    if (!user) return;
    const fetchCount = () =>
      api.get("/trades/pending-count").then((res) => setPendingTrades(res.data.count)).catch(() => {});
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    setThemeState(next);
  };

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/");
  };

  const closeMenu = () => setMenuOpen(false);

  const currencySelect = (testId) => currencyCtx && (
    <Select value={currencyCtx.currency} onValueChange={currencyCtx.setCurrency}>
      <SelectTrigger
        data-testid={testId}
        className="h-8 w-[86px] rounded-full bg-transparent border-white/15 text-xs font-semibold px-3"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.keys(currencyCtx.symbols || { USD: "$" }).map((c) => (
          <SelectItem key={c} data-testid={`currency-option-${c}`} value={c}>{c}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <nav
      data-testid="main-navbar"
      className="sticky top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-2 group shrink-0">
          <Layers3 className="w-6 h-6 text-[#A78BFA] transition-transform group-hover:-translate-y-0.5" />
          <span className="font-display font-800 text-lg tracking-tight text-white whitespace-nowrap">Card<span className="text-[#A78BFA]">Ark</span></span>
        </Link>

        {/* Desktop */}
        <div className="hidden sm:flex items-center gap-4">
          <Link to="/guides" data-testid="navbar-guides-link" className="text-sm text-zinc-300 hover:text-white transition-colors">
            Guides
          </Link>
          <Link to="/community" data-testid="navbar-community-link" className="text-sm text-zinc-300 hover:text-white transition-colors">
            Explore
          </Link>
          {user ? (
            <>
              <Link to="/upload" data-testid="navbar-upload-link" className="text-sm text-zinc-300 hover:text-white transition-colors">
                Scan Cards
              </Link>
              <Link to="/binder" data-testid="navbar-binder-link" className="text-sm text-zinc-300 hover:text-white transition-colors">
                Dashboard
              </Link>
              <Link to="/sales" data-testid="navbar-sales-link" className="text-sm text-zinc-300 hover:text-white transition-colors">
                Ledger
              </Link>
              <Link
                to="/trades"
                data-testid="navbar-trades-link"
                title="Trade pings"
                className="relative text-zinc-300 hover:text-white transition-colors"
              >
                <ArrowLeftRight className="w-4 h-4" />
                {pendingTrades > 0 && (
                  <span data-testid="navbar-trades-badge" className="absolute -top-2 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[#F59E0B] text-black text-[10px] font-bold flex items-center justify-center">
                    {pendingTrades}
                  </span>
                )}
              </Link>
              {currencySelect("navbar-currency-select")}
              <Link
                to="/billing"
                data-testid="navbar-credits-badge"
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-[#A78BFA]/30 bg-[#A78BFA]/10 text-[#A78BFA] hover:bg-[#A78BFA]/20 transition-colors"
              >
                <Zap className="w-3.5 h-3.5" /> {user.unlimited_credits ? "∞" : user.credits_balance ?? "—"}
              </Link>
            </>
          ) : null}
          <Button
            data-testid="theme-toggle-button"
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            title={theme === "light" ? "Switch to night mode" : "Switch to day mode"}
            className="text-zinc-400 hover:text-white h-8 w-8"
          >
            {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          </Button>
          {user && (
            <>
              <ActiveScanPill />
              <NotificationsBell />
              <Link to="/settings" data-testid="navbar-settings-link" title="Settings" className="text-zinc-400 hover:text-white transition-colors">
                <SettingsIcon className="w-4 h-4" />
              </Link>
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-white/20" data-testid="navbar-user-avatar" />
              <Button
                data-testid="navbar-logout-button"
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-zinc-400 hover:text-white"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="flex sm:hidden items-center gap-2">
          {user && <ActiveScanPill />}
          {user && <NotificationsBell />}
          {user && (
            <Link
              to="/billing"
              data-testid="navbar-credits-badge-mobile"
              onClick={closeMenu}
              className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border border-[#A78BFA]/30 bg-[#A78BFA]/10 text-[#A78BFA]"
            >
              <Zap className="w-3.5 h-3.5" /> {user.unlimited_credits ? "∞" : user.credits_balance ?? "—"}
            </Link>
          )}
          <Button
            data-testid="navbar-mobile-menu-button"
            variant="ghost"
            size="icon"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            className="text-zinc-300 hover:text-white h-9 w-9"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {menuOpen && (
        <div data-testid="navbar-mobile-menu" className="sm:hidden border-t border-white/10 bg-black/60 backdrop-blur-xl px-4 py-4 space-y-1 animate-fade-up">
          <Link to="/guides" data-testid="navbar-mobile-guides-link" onClick={closeMenu} className="block px-3 py-2.5 rounded-lg text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors">
            Guides
          </Link>
          <Link to="/community" data-testid="navbar-mobile-community-link" onClick={closeMenu} className="block px-3 py-2.5 rounded-lg text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors">
            Explore
          </Link>
          {user && (
            <>
              <Link to="/upload" data-testid="navbar-mobile-upload-link" onClick={closeMenu} className="block px-3 py-2.5 rounded-lg text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors">
                Scan Cards
              </Link>
              <Link to="/binder" data-testid="navbar-mobile-binder-link" onClick={closeMenu} className="block px-3 py-2.5 rounded-lg text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors">
                Dashboard
              </Link>
              <Link to="/sales" data-testid="navbar-mobile-sales-link" onClick={closeMenu} className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors">
                <Receipt className="w-4 h-4" /> Sales Ledger
              </Link>
              <Link to="/trades" data-testid="navbar-mobile-trades-link" onClick={closeMenu} className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors">
                <span>Trades</span>
                {pendingTrades > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#F59E0B] text-black text-[10px] font-bold flex items-center justify-center">{pendingTrades}</span>
                )}
              </Link>
              <Link to="/settings" data-testid="navbar-mobile-settings-link" onClick={closeMenu} className="block px-3 py-2.5 rounded-lg text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors">
                Settings
              </Link>
              <Link to="/billing" data-testid="navbar-mobile-billing-link" onClick={closeMenu} className="block px-3 py-2.5 rounded-lg text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors">
                Credits &amp; Billing
              </Link>
            </>
          )}
          <div className="flex items-center justify-between px-3 py-2.5">
            <span className="text-sm text-zinc-400">Currency</span>
            {currencyCtx && (
              <select
                data-testid="navbar-mobile-currency-select"
                value={currencyCtx.currency}
                onChange={(e) => currencyCtx.setCurrency(e.target.value)}
                className="h-8 rounded-full bg-transparent border border-white/15 text-xs font-semibold px-3 pr-6 text-zinc-200 focus:outline-none appearance-none"
              >
                {Object.keys(currencyCtx.symbols || { USD: "$" }).map((c) => (
                  <option key={c} value={c} className="bg-[#111113] text-zinc-200">{c}</option>
                ))}
              </select>
            )}
          </div>
          <div className="flex items-center justify-between px-3 py-2.5">
            <span className="text-sm text-zinc-400">{theme === "light" ? "Day mode" : "Night mode"}</span>
            <Button
              data-testid="theme-toggle-button-mobile"
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="text-zinc-400 hover:text-white h-8 w-8"
            >
              {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </Button>
          </div>
          {user && (
            <div className="flex items-center justify-between px-3 pt-3 border-t border-white/10">
              <div className="flex items-center gap-2.5 min-w-0">
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-white/20 shrink-0" data-testid="navbar-mobile-user-avatar" />
                <span className="text-sm text-zinc-300 truncate">{user.name}</span>
              </div>
              <Button
                data-testid="navbar-mobile-logout-button"
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-zinc-400 hover:text-white shrink-0"
              >
                <LogOut className="w-4 h-4 mr-1.5" /> Log out
              </Button>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
